import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { ResponsiveImage } from '../../components/ResponsiveImage/ResponsiveImage'
import { ProtectedImage } from '../../components/ProtectedImage/ProtectedImage'
import { SectionLabel } from '../../components/SectionLabel/SectionLabel'
import { SectionScaffold } from '../../components/SectionScaffold/SectionScaffold'
import { assets } from '../../data/assets'
import { content } from '../../data/content'
import {
  createMotionMedia,
  gsap,
  initializeWhenNear,
  motionConditions,
  motionTokens,
  type MotionConditionState,
} from '../../motion'
import styles from './CultureDisplaySection.module.css'

const galleryItems = [
  { asset: assets['AST-013'], caption: '设计稿' },
  { asset: assets['AST-011'], caption: '效果图' },
  { asset: assets['AST-012'], caption: '现场实景融合' },
] as const

export function CultureDisplaySection() {
  const introRef = useRef<HTMLElement>(null)
  const carouselRef = useRef<HTMLDivElement>(null)
  const carouselRevealRef = useRef<HTMLDivElement>(null)
  const closeTimerRef = useRef<number | null>(null)
  const dragStateRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    scrollLeft: 0,
  })
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [isLightboxClosing, setIsLightboxClosing] = useState(false)
  const copy = content['SEC-005']

  const closeLightbox = useCallback(() => {
    if (lightboxIndex === null) return

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setLightboxIndex(null)
      return
    }

    setIsLightboxClosing(true)
    closeTimerRef.current = window.setTimeout(() => {
      setLightboxIndex(null)
      setIsLightboxClosing(false)
      closeTimerRef.current = null
    }, 260)
  }, [lightboxIndex])

  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (lightboxIndex === null) return

    const previousOverflow = document.body.style.overflow
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeLightbox()
    }

    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [closeLightbox, lightboxIndex])

  useLayoutEffect(() => {
    const intro = introRef.current
    const carousel = carouselRevealRef.current
    if (!intro || !carousel) return

    return initializeWhenNear(intro, () => {
      const media = createMotionMedia()

      media.add(motionConditions, (context) => {
        const { desktop, reduceMotion } =
          context.conditions as MotionConditionState

        if (reduceMotion) {
          gsap.set([intro, carousel], {
            clearProps: 'all',
            autoAlpha: 1,
            y: 0,
          })
          return
        }

        gsap.fromTo(
          intro,
          { autoAlpha: 0, y: motionTokens.distance.small },
          {
            autoAlpha: 1,
            y: 0,
            duration: motionTokens.duration.normal,
            ease: motionTokens.ease.entrance,
            scrollTrigger: {
              trigger: intro,
              start: desktop ? 'top 86%' : 'top 93%',
              once: true,
            },
          },
        )

        gsap.fromTo(
          carousel,
          { autoAlpha: 0, y: motionTokens.distance.medium },
          {
            autoAlpha: 1,
            y: 0,
            duration: motionTokens.duration.slow,
            ease: motionTokens.ease.entrance,
            scrollTrigger: {
              trigger: carousel,
              start: desktop ? 'top 88%' : 'top 94%',
              once: true,
              invalidateOnRefresh: true,
            },
          },
        )
      })

      return () => media.revert()
    })
  }, [])

  const openLightbox = (index: number) => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
    setIsLightboxClosing(false)
    setLightboxIndex(index)
  }

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return

    const carousel = carouselRef.current
    if (!carousel) return

    dragStateRef.current = {
      active: true,
      moved: false,
      startX: event.clientX,
      scrollLeft: carousel.scrollLeft,
    }
    carousel.setPointerCapture(event.pointerId)
    carousel.classList.add(styles.isDragging)
  }

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const carousel = carouselRef.current
    if (!carousel || !dragStateRef.current.active) return

    event.preventDefault()
    if (Math.abs(event.clientX - dragStateRef.current.startX) > 6) {
      dragStateRef.current.moved = true
    }
    carousel.scrollLeft =
      dragStateRef.current.scrollLeft -
      (event.clientX - dragStateRef.current.startX)
  }

  const stopDragging = (event: ReactPointerEvent<HTMLDivElement>) => {
    const carousel = carouselRef.current
    if (!carousel || !dragStateRef.current.active) return

    dragStateRef.current.active = false
    carousel.classList.remove(styles.isDragging)
    if (carousel.hasPointerCapture(event.pointerId)) {
      carousel.releasePointerCapture(event.pointerId)
    }
  }

  return (
    <SectionScaffold
      id="SEC-005"
      className={styles.section}
      labelledBy="sec-005-title"
    >
      <div className={styles.motionRoot}>
        <header ref={introRef} className={styles.intro}>
          <SectionLabel>— 05 / CULTURE DISPLAY</SectionLabel>
          <h2 id="sec-005-title" className={styles.title}>
            {copy.title}
          </h2>
          <p className={styles.subtitle}>{copy.subtitle}</p>
          <p className={styles.body}>{copy.body}</p>
          <p className={styles.data}>{copy.data}</p>
        </header>

        <div ref={carouselRevealRef} className={styles.carouselViewport}>
          <div
            ref={carouselRef}
            className={styles.carouselTrack}
            aria-label="文化展示设计推演画廊"
            tabIndex={0}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={stopDragging}
            onPointerCancel={stopDragging}
          >
            {galleryItems.map((item, index) => (
              <figure className={styles.carouselCard} key={item.asset.id}>
                <button
                  className={styles.imageButton}
                  type="button"
                  aria-label={`全屏查看${item.caption}`}
                  onClick={(event) => {
                    if (dragStateRef.current.moved) {
                      event.preventDefault()
                      dragStateRef.current.moved = false
                      return
                    }
                    openLightbox(index)
                  }}
                >
                  <ResponsiveImage
                    className={styles.cardImage}
                    src={item.asset.path}
                    alt={item.asset.alt}
                    width={item.asset.width}
                    height={item.asset.height}
                    loading="lazy"
                    draggable="false"
                    sizes="(max-width: 47.9375rem) 77vw, (max-width: 63.9375rem) 42vw, 38vw"
                  />
                </button>
                <figcaption className={styles.caption}>
                  <span className={styles.captionTitle}>{item.caption}</span>
                  <span className={styles.captionHint}>点击看大图</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>

      {lightboxIndex !== null ? (
        <div
          className={`${styles.lightbox} ${
            isLightboxClosing ? styles.lightboxClosing : ''
          }`}
          role="dialog"
          aria-modal="true"
          aria-label={`${galleryItems[lightboxIndex].caption}大图`}
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) closeLightbox()
          }}
        >
          <button
            className={styles.lightboxClose}
            type="button"
            aria-label="关闭大图"
            onClick={closeLightbox}
            autoFocus
          >
            <span aria-hidden="true" />
          </button>
          <figure className={styles.lightboxFigure}>
            <ProtectedImage
              className={styles.lightboxImage}
              src={galleryItems[lightboxIndex].asset.path}
              alt={galleryItems[lightboxIndex].asset.alt}
              width={galleryItems[lightboxIndex].asset.width}
              height={galleryItems[lightboxIndex].asset.height}
              draggable={false}
            />
            <figcaption className={styles.lightboxCaption}>
              {galleryItems[lightboxIndex].caption}
            </figcaption>
          </figure>
        </div>
      ) : null}
    </SectionScaffold>
  )
}
